import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { usersApi } from "../lib/usersApi";
import type { UpdateUserPayload } from "../lib/usersApi";

export const USER_KEYS = {
  all: ["users"] as const,
  list: (search: string) => [...USER_KEYS.all, "list", search] as const,
  detail: (id: string) => [...USER_KEYS.all, "detail", id] as const,
};

export function useUsers(search = "", enabled = true) {
  return useInfiniteQuery({
    queryKey: USER_KEYS.list(search),
    queryFn: ({ pageParam }) =>
      usersApi.list(25, pageParam as string | undefined, search || undefined),
    getNextPageParam: (lastPage) => lastPage.nextKey ?? undefined,
    initialPageParam: undefined as string | undefined,
    enabled,
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: usersApi.create,
    onSuccess: () => qc.invalidateQueries({ queryKey: USER_KEYS.all }),
  });
}

export function useUpdateUser(userId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateUserPayload) => usersApi.update(userId, payload),
    onSuccess: () => qc.refetchQueries({ queryKey: USER_KEYS.all, type: "all" }),
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: usersApi.deactivate,
    onSuccess: () => qc.invalidateQueries({ queryKey: USER_KEYS.all }),
  });
}

export function useReactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: usersApi.reactivate,
    onSuccess: () => qc.invalidateQueries({ queryKey: USER_KEYS.all }),
  });
}

export function useDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: usersApi.deleteUser,
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: USER_KEYS.all }),
      qc.invalidateQueries({ queryKey: ["biz"] }),
      qc.invalidateQueries({ queryKey: ["businesses"] }),
    ]),
  });
}
